import React, { useMemo } from 'react';
import i18next from 'i18n';
import styles from './addon.module.scss';
import { InfoDisplaySection } from 'components/InfoDisplaySection/InfoDisplaySection';
import { AddonDependency } from 'core/agency/AddonFeature';
import _ from 'lodash';
import { multiply100 } from 'utils/Math';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { getChannelGroupName } from 'components/AgencyForm/agencyAddonVisibility';
import { getSalesChannelName } from 'utils/I18nUtils';
import { AgencyDetail } from 'core';

type AddonProps = {
  agencyDetail: AgencyDetail;
};

// per-channel 扁平 key 格式：`channel_${channelGroupId}_${addonKey}`
const CHANNEL_BASED_ADDON_KEY = /^channel_(\d+)_(.+)$/;

function addonProps(agencyDetail, addonGroup, addonItem): any {
  if (addonItem === 'singleOrderBudgetConstraint') {
    return (
      i18next.t('agency.addonItem.company.orderBudgetMaximum') +
      ' ' +
      agencyDetail['property']['addonProps']['orderBudgetMaximum']
    );
  }
  if (addonItem === 'creditLimit') {
    return (
      i18next.t('agency.addonItem.company.creditLimitMaximum') +
      ' ' +
      agencyDetail['property']['addonProps']['creditLimit']
    );
  }
  if (addonItem === 'fixedExchangeRate') {
    return (
      i18next.t('agency.addonItem.company.ExchangeRate') +
      ' ' +
      agencyDetail['property']['addonProps']['fixedExchangeRate']
    );
  }
  if (addonItem === 'agencySegment') {
    return i18next.t('limitation.labels.agencySegment', {
      name: agencyDetail['property']['addonProps'][
        'agencySegmentLimitationName'
      ]
    });
  }
}

function isAddonAllFalse(addonGroup, excludeList) {
  let result = true;
  if (!addonGroup) {
    return result;
  }
  Object.keys(addonGroup).forEach(function (addonItem) {
    if (addonGroup[addonItem] === true && !excludeList.includes(addonItem)) {
      result = false;
    }
  });

  return result;
}

const Addon: React.FC<AddonProps> = ({ agencyDetail }) => {
  const keylist = [
    'channel',
    'creatives',
    'company',
    'campaign',
    'campaignLimitation'
  ];
  const excludeList = [
    'bidPriceBaseVal',
    'bidPriceCPCMin',
    'bidPriceCPMMin',
    'bidPriceCPVMin',
    'bidPricevCPMMin',
    'bidPriceConstraint',
    'booking_calendar',
    'campaignDashboard',
    'habits',
    'option_6',
    'orderBudgetMaximum',
    'incentive',
    'landingPage',
    'campaignUnderBidFloor',
    'deliveryAmountConstraint'
  ];

  const creativeTypeSettings = useMemo(
    () => ChannelGroupSetting.creativeTypeSettings(true),
    []
  );

  const addonData = keylist.map((addonGroup) => {
    if (
      isAddonAllFalse(agencyDetail['addonFeatures'][addonGroup], excludeList)
    ) {
      return {
        sectionTitle: i18next.t('agency.addonGroup.' + addonGroup),
        sectionData: []
      };
    } else {
      const channelAddonConfigs = ChannelGroupSetting.getAddonConfigs();
      return {
        sectionTitle: i18next.t('agency.addonGroup.' + addonGroup),
        sectionData: _.compact(
          Object.keys(agencyDetail['addonFeatures'][addonGroup])
            .filter(
              (addonItem) =>
                agencyDetail['addonFeatures'][addonGroup][addonItem] === true &&
                !excludeList.includes(addonItem) &&
                (AddonDependency[addonItem] === undefined ||
                  agencyDetail['addonFeatures'][addonGroup][
                    AddonDependency[addonItem]
                  ] === true)
            )
            .map((addonItem) => {
              // per-channel 扁平 key（channel_${id}_${addonKey}）：i18n 只用
              // addonKey 組，label 後綴加上該 channel group 名稱。
              const channelMatch = CHANNEL_BASED_ADDON_KEY.exec(addonItem);
              const i18nkey = channelMatch ? channelMatch[2] : addonItem;
              const channelGroup = channelMatch
                ? ChannelGroupSetting.values().find(
                    (group) => group.config.id === Number(channelMatch[1])
                  )
                : undefined;
              let label = i18next.t(
                `agency.addonItem.${addonGroup}.${i18nkey}`
              );
              if (!channelGroup && channelMatch) {
                return undefined;
              }
              // 停用渠道的 per-channel 項目不顯示
              if (channelGroup?.isEffectivelyArchived()) {
                return undefined;
              }
              if (channelGroup) {
                label = `${label} (${getChannelGroupName(channelGroup)})`;
              }
              let value = addonProps(agencyDetail, addonGroup, addonItem);
              if (addonItem in channelAddonConfigs) {
                const { channelId, channelName } =
                  channelAddonConfigs[addonItem];
                // ECOPD-5566：停用渠道相關的 add-on 不顯示。
                // 後端關閉 agency 旗標的批次若漏掉某個 agency，這裡的旗標會留著
                // true —— 顯式擋掉，不依賴資料乾淨。
                if (
                  channelId !== undefined &&
                  ChannelGroupSetting.isEffectivelyArchivedChannel(channelId)
                ) {
                  return undefined;
                }
                label = getSalesChannelName(channelId ?? '', channelName);
                const channelMargin = _.get(
                  agencyDetail,
                  `property.addonProps.channelMargin.${channelId}`
                );
                value = _.isNil(channelMargin)
                  ? undefined
                  : `${i18next.t(
                      'agency.addonItem.channel.salesChannelMargin'
                    )} ${_.round(multiply100(channelMargin), 2)}%`;
              } else if (_.startsWith(addonItem, 'option_')) {
                const creativeType = addonItem.replace('option_', '');
                const creativeTypeSetting =
                  creativeTypeSettings.get(+creativeType);
                // 找不到素材類型設定（例如該類型已下架）就不顯示
                if (!creativeTypeSetting) {
                  return undefined;
                }
                // ECOPD-5572：停用渠道底下的素材類型也不顯示。
                // 後端停用渠道時只關掉渠道自己那顆 add-on，底下的素材類型旗標
                // 會留著 true；而 `creativeTypeSettings` 是從所有渠道（含停用）
                // 攤平出來的，所以查得到名稱、不擋就會顯示 —— 同一頁的渠道那一列
                // 已經被擋掉了，不擋這裡會變成「渠道不見了、它的素材類型還在」。
                if (
                  ChannelGroupSetting.isEffectivelyArchivedChannel(
                    creativeTypeSetting.channelGroupId
                  )
                ) {
                  return undefined;
                }
                label = creativeTypeSetting.name;
              } else if (
                addonGroup === 'channel' &&
                !isNaN(Number(addonItem)) &&
                !(addonItem in channelAddonConfigs)
              ) {
                // ECOPD-5566：渠道 id 在 registry 裡找不到（例如渠道已被刪除）
                // 就不顯示。停用渠道**仍然會**被註冊，改由上面的
                // `isEffectivelyArchivedChannel` 顯式擋掉。
                return undefined;
              }
              return {
                label,
                value
              };
            })
        )
      };
    }
  });
  return (
    <div className={styles.container}>
      {addonData.map((addonGroup) => {
        return (
          <InfoDisplaySection
            key={addonGroup.sectionTitle}
            data={addonGroup.sectionData}
            title={addonGroup.sectionTitle}
          />
        );
      })}
    </div>
  );
};

export default Addon;
